import { until } from "@vueuse/core";
import { useInsuranceFilterStore } from "#ce/shared/filters/insurance-plan/store/insurance-filter";

// How long we keep the finder blocked on plan validation before failing closed — a deadlock guard, not
// an API SLA. The validation awaits the plan-list fetch (which gates finder fetching), so a stalled
// request must never freeze the finder forever. Deliberately generous so a merely slow load still
// validates rather than failing open. Kept local to this composable (the shared API client has no
// timeout of its own).
const PLAN_VALIDATION_DEADLOCK_TIMEOUT_MS = 15_000;

/**
 * Validates insurance_plan slugs against the plan list, single-sourcing the lookup through the insurance
 * filter store (the same `load` + `findByValue` the filter's own self-heal uses).
 *
 * `resolveFirstValid(candidates)` takes candidates in precedence order (each `{ value, onInvalid }`) and
 * returns the first one that resolves to a known plan; otherwise `undefined`. It loads the plan list once.
 * For every candidate it passes that loaded successfully but isn't a real plan, it invokes that
 * candidate's own `onInvalid` so the caller can clear *that specific source* — this is what lets an invalid
 * stored plan be cleared while a valid URL plan still wins. When the plan list can't be loaded
 * (timeout/failure) we can't validate anything, so we fail closed — return `undefined` and clear nothing,
 * since a junk slug must never bypass a required modal but a possibly-real value shouldn't be wiped on a
 * transient error (each source's self-heal clears a junk one once its list loads).
 */
export const useInsurancePlanValidation = () => {
  // Validate against the insurance filter store. Called with no options, so this builds its own store
  // instance — the store factory creates fresh state per call, so this does NOT share the
  // InsurancePlanFilter's store — and validates against the default provider-search insurance endpoint
  // (INSURANCE_FILTER_DEFAULT_*). A finder pointed at a non-default endpoint would need its filter
  // config threaded through here to validate against the matching plan set.
  const store = useInsuranceFilterStore();

  async function resolveFirstValid(candidates = []) {
    const present = candidates.filter((candidate) => candidate?.value);
    if (!present.length) {
      return undefined;
    }

    // Load the plan list once. The guard only skips a redundant load within this same store instance —
    // when it already has a load in flight or the candidate is already known here. It cannot dedupe
    // against the filter's separate store, which runs its own load.
    if (!store.loading.value && !present.some((candidate) => store.findByValue(candidate.value))) {
      void store.load(present[0].value);
    }

    try {
      await until(store.loading).toBe(false, {
        timeout: PLAN_VALIDATION_DEADLOCK_TIMEOUT_MS,
        throwOnTimeout: true,
      });
    } catch {
      return undefined; // timed out — couldn't validate; fail closed, clear nothing
    }

    if (store.error.value) {
      return undefined; // load failed — couldn't validate; fail closed, clear nothing
    }

    for (const candidate of present) {
      if (store.isSelectable(candidate.value)) {
        return candidate.value; // first real, *selectable* plan in precedence order
      }
      // Loaded fine, but this slug isn't a selectable leaf plan (unknown, or a display-only group
      // parent like `group-employer-plans`) — clear its source so a parent slug can't skip the modal.
      candidate.onInvalid?.();
    }
    return undefined;
  }

  return { resolveFirstValid };
};
